import { fetchDataV2, postDataV2 } from "./utils";

// GET/POST /faq are live (docs/needed-endpoints.md §4). `answer` is a plain string here, not the
// hand-authored rich JSX FaqAccordion.tsx used to render - so the copy differs slightly across
// paragraphs/lists that were split into rendered elements. `related_dashboard_id` is still a real
// field on the response but is no longer read anywhere - FaqAccordion.tsx used to cross-reference
// it against the dashboard registry to render a "Go to dashboard" link; that link was removed.
export interface FaqItem {
  id: number;
  question: string;
  answer: string;
  related_dashboard_id: number | null;
  external_link: string | null;
  category: string;
  display_order: number;
  is_active: boolean;
}

export const getFaqItems = (): Promise<FaqItem[]> => fetchDataV2("/faq").then((response) => (Array.isArray(response) ? response : []));

// Live - admin-managed, no create/edit UI exists yet (docs/needed-endpoints.md §4 always described
// this as script/API-managed, no UI this phase) - wired ahead of time matching this codebase's
// usual pattern.
export const createFaqItem = (payload: Omit<FaqItem, "id">): Promise<FaqItem> => postDataV2("/faq", payload);

// The single document backing UserGuideTileContent.tsx's PDF (previously a static bundled asset).
// getFaqDocument() (the metadata-only GET /faq/document counterpart to the blob fetch below) was
// removed once UserGuideTileContent.tsx stopped forcing a named download - it only ever fetched
// this for the file's real name to pass to the <a download> attribute.
export interface FaqDocument {
  id: number;
  file_name: string;
  content_type: string;
  size_bytes: number;
  uploaded_at: string;
  download_path: string;
}

// Documented response type is "string"/the FaqDocument metadata shape (this pair's docs keep
// showing a generic/placeholder response model, not what the endpoint actually streams), but this
// is the actual PDF content, rendered in an iframe and offered as a direct download - fetched as a
// blob (not read as text/JSON) so it can be turned into an object URL, same as any other binary
// file this app handles. Needs the same bearer-token auth as every other call here, which a plain
// <a href>/<iframe src> pointed straight at the backend wouldn't carry.
export const getFaqDocumentBlob = (): Promise<Blob> => fetchDataV2("/faq/document/download", undefined, "blob");

// GlossaryTileContent.tsx's XLSX - live now (was a "#" placeholder). Confirmed directly from the
// backend's own route/schema files (cedar-backend's feat-2/cedar-restructure branch) - same
// FaqDocument-shaped metadata pair as the User Guide PDF above, just under /faq/glossary/... `id`
// is always 0 server-side (the glossary file is read straight from GCS, no metadata row to key
// on - kept only for shape-parity with FaqDocument).
export const getGlossaryDocument = (): Promise<FaqDocument> => fetchDataV2("/faq/glossary/document");

export const getGlossaryDocumentBlob = (): Promise<Blob> => fetchDataV2("/faq/glossary/document/download", undefined, "blob");
